<template>
  <div class="text-box">
    <h2 class="m-b-20 section-title">{{ $t('additional_information') }}</h2>
    <div class="card">
      <!-- INCOTERMS as radio buttons -->
      <div class="row m-b-20" v-if="showIncoterms">
        <div class="col-xs-12">
          <div class="radio-label">{{ $t('Incoterms') }}</div>
          <div class="radio-group">
            <RadioButton
              v-for="option in getIncotermOptions"
              :key="option.value"
              name="incoterms"
              :text="option.text"
              :value="option.value"
              :checked="value.goods_incoterm === option.value"
              @input="checkDutyAndCarrierNo(option.value)"
            />
          </div>
        </div>
      </div>

      <!-- DUTY PAID BY + FREIGHT PAID BY always on the same row -->
      <div class="row" v-if="showIncoterms || showFreightBy">
        <!-- DUTY PAID BY -->
        <div
          v-if="value.goods_incoterm === 'DDP' && showIncoterms"
          :class="[classDutyPaidBy, 'm-b-20']">
          <InputSelect
            :label="$t('duty_paid_by')"
            v-model="value.duty_paid_by"
            @change="checkCarrierNo"
            :options="dutyPaidByOptions"
          />
        </div>
        <!-- CARRIER NUMBER (duty) -->
        <div
          v-if="(value.duty_paid_by === 3 || value.duty_paid_by === 2) && showIncoterms && value.goods_incoterm === 'DDP'"
          :class="[classDutyCarrierNo, 'm-b-20']">
          <InputText
            v-model="value.duty_payer_account_no"
            :label="$t('Carrier account no.')"
          />
        </div>
        <!-- FREIGHT PAID BY -->
        <div
          v-if="showFreightBy"
          :class="[classFreightBy, 'm-b-20']">
          <InputSelect
            :label="$t('freight_paid_by')"
            v-model="value.freight_paid_by"
            @change="checkFreightandCustomerInfo"
            :options="freightPaidByOptions"
          />
        </div>
        <!-- FREIGHT CARRIER NUMBER -->
        <div
          v-if="value.freight_paid_by === 2 || value.freight_paid_by === 3"
          :class="[classFreightCarrierNo, 'm-b-20']">
          <InputText
            v-model="value.freight_payer_account_no"
            :label="$t('Freight Carrier account no.')"
          />
        </div>
        <!-- FREIGHT PAYER SELECT -->
        <div
          v-if="value.freight_paid_by === 4"
          :class="[classFreightBy, 'm-b-20']">
          <InputSelect
            :label="$t('LBL_PAYERS')"
            v-model="value.freight_payer_id"
            :options="get_payer_select"
          />
        </div>
      </div>

      <div v-if="showCustoms">
        <div class="m-b-20">
          <!-- Clearance by + Reason for export + Additional info (+ broker if broker selected) -->
          <div class="row m-b-20">
            <div :class="[infoTopRowColClass, 'm-b-10']">
              <InputSelect
                :label="$t('clearance')"
                v-model="value.clearance_by"
                @change="onClearanceByChange"
                :options="clearanceByOptions"
              />
            </div>
            <!-- Broker selector — only when clearance_by === 'broker' (4th field → col-xs-3) -->
            <div v-if="value.clearance_by === 'broker'" :class="[infoTopRowColClass, 'm-b-10']">
              <InputSelect
                :label="$t('broker')"
                @change="onBrokerChange"
                v-model="value.broker_id"
                :options="brokerOptions"
              />
            </div>
            <div :class="[infoTopRowColClass, 'm-b-10']">
              <InputSelect
                :label="$t('LBL_REASON_FOR_EXPORT')"
                v-model="additionalInfo.invoiceType"
                :options="invoiceTypeOptions"
                @change="updateAdditionalInfo('invoiceType', $event)"
              />
            </div>
            <div :class="[infoTopRowColClass, 'm-b-10']">
              <InputSelect
                :multiSelect="true"
                :log="true"
                :preselected="selectedInfoItems"
                :label="$t('Additional info')"
                @input="selectAdditionalInfo"
                :options="additionalInfoOptions"
              />
            </div>

          </div>

          <!-- ECCN / TEXT / MRN – dynamic grid (1→col-xs-12, 2→col-xs-6, 3→col-xs-4) -->
          <div class="row m-b-10" v-if="nonEeiSelectedCount > 0">
            <div
              v-if="selectedInfoItems.indexOf('2') > -1"
              :class="nonEeiColClass">
              <InputText
                :label="$t('ECCN number')"
                :value="additionalInfo.eccn"
                @input="updateAdditionalInfo('eccn', $event)"
              />
            </div>
            <div
              v-if="selectedInfoItems.indexOf('22') > -1"
              :class="nonEeiColClass">
              <InputText
                :label="$t('Text')"
                :value="additionalInfo.customsAdditionalInfoText"
                @input="updateAdditionalInfo('customsAdditionalInfoText', $event)"
              />
            </div>
            <div
              v-if="selectedInfoItems.indexOf('4') > -1"
              :class="nonEeiColClass">
              <InputText
                :label="$t('MRN number')"
                :value="additionalInfo.mrn"
                @input="updateAdditionalInfo('mrn', $event)"
              />
            </div>
          </div>

          <!-- EEI – always its own row, 3 fields each col-xs-4 -->
          <div class="row m-b-10" v-if="selectedInfoItems.indexOf('3') > -1">
            <div class="col-xs-4">
              <InputText
                :label="$t('EEI number')"
                :value="additionalInfo.eeiitn.eei"
                @input="updateNestedAdditionalInfo('eeiitn', 'eei', $event)"
              />
            </div>
            <div class="col-xs-4">
              <InputText
                :label="$t('Tin number')"
                :value="additionalInfo.eeiitn.tinNumber"
                @input="updateNestedAdditionalInfo('eeiitn', 'tinNumber', $event)"
              />
            </div>
            <div class="col-xs-4">
              <InputSelect
                :label="$t('Tin type')"
                :value="additionalInfo.eeiitn.tinType"
                :options="tinTypeOptions"
                @input="updateNestedAdditionalInfo('eeiitn', 'tinType', $event)"
              />
            </div>
          </div>
        </div>

      </div>
      <div v-if="showUploadForm && showCustoms">
        <document-uploader
          @file-uploaded="$emit('file-uploaded', $event)"
          @document-type-changed="$emit('document-type-changed', $event)"
          :uploadedFiles="invoiceFiles"
          :initialDocumentType="documentType"
          :showDangerousGoods="showDangerousGoods"
        />
      </div>
    </div>
  </div>
</template>

<script>
/*eslint-disable*/
import mixin from '@/mixins/mixins.js';
import InputText from "@/components/elements/inputs/text.vue";
import InputSelect from "@/components/elements/inputs/select.vue";
import RadioButton from "@/components/elements/inputs/radio.vue";
import AddressPicker from "@/components/global/addressPicker/index.vue";
import PhoneSelect from "@/components/elements/inputs/phone.vue";
import DocumentUploader from "./documentUploader.vue";
import {mapGetters} from "vuex";
import i18n from "@/i18n";

export default {
  name: 'additional-information',
  mixins: [mixin],
  components: {
    InputSelect,
    InputText,
    RadioButton,
    AddressPicker,
    PhoneSelect,
    DocumentUploader
  },
  props: {
    invoiceFiles: {
      type: Array,
      required: false,
      default: () => []
    },
    documentType: {
      type: String,
      default: 'CINV'
    },
    showFreightBy: {
      type: Boolean,
      default: true,
    },
    showCustoms: {
      type: Boolean,
      default: true,
    },
    // Dangerous goods shipments may upload their own DG document in the customs box; ordinary
    // declarable shipments do not get the DG option.
    showDangerousGoods: {
      type: Boolean,
      default: false,
    },
    showIncoterms: {
      type: Boolean,
      default: true,
    },
    showUploadForm: {
      type: Boolean,
      default: true,
    },
    availableIncoterms: {
      type: Array,
      default: () => [],
    },
    additionalInfo: {
      type: Object,
      required: true
    },
    selectedInfoItems: {
      type: Array,
      required: true,
    },
    value: {
      type: Object,
      default: () => {
        return {
          goods_incoterm: '',
          duty_paid_by: '',
          duty_payer_account_no: '',
          freight_paid_by: '',
          freight_payer_account_no: '',
          clearance_by: '',
          broker_id: '',
        }
      }
    },
  },
  data() {
    return {
      incotermServices: [],
      incotermServiceId: '',
      reciverPayId: '',
      brokerServiceId: '',
      incotermsOptions: [
        { text: `${this.$t('DAP')}`, value: 'DAP'},
        { text: `${this.$t('DDP')}`, value: 'DDP'},
        { text: `${this.$t('EXW')}`, value: 'EXW'},
        // { text: `${this.$t('FCA')}`, value: 'FCA'},
        //{ text: `${this.$t('CPT')}`, value: 'CPT'},
        // { text: `${this.$t('CIP')}`, value: 'CIP'},
        // { text: `${this.$t('DPU')}`, value: 'DPU'},
      ],
      dutyPaidByOptions: [
        { text: `${this.$t('Seller')}`, value: 1},
        { text: `${this.$t('Reciever')}`, value: 2},
        { text: `${this.$t('Third party')}`, value: 3},
      ],
      additionalInfoOptions: [
        { text: 'ECCN', value: '2' },
        { text: 'EEI',  value: '3' },
        { text: 'TEXT', value: '22' },
        { text: 'MRN',  value: '4' },
      ],
      tinTypeOptions: [
        { text: `${i18n.t('BUSINESS_NATIONAL')}`, value: 1},
        { text: `${i18n.t('BUSINESS_STATE')}`, value: 2},
        { text: `${i18n.t('BUSINESS_UNION')}`, value: 3},
        { text: `${i18n.t('PERSONAL_NATIONAL')}`, value: 4},
        { text: `${i18n.t('PERSONAL_STATE')}`, value: 5},
      ],
      invoiceTypeOptions: [
        { text: `${i18n.t('Sample')}`, value: 1},
        { text: `${i18n.t('Sale')}`, value: 2},
        { text: `${i18n.t('Gift')}`, value: 3},
        { text: `${i18n.t('Return_1')}`, value: 4},
        { text: `${i18n.t('Repair')}`, value: 5},
        { text: `${i18n.t('Temporary export')}`, value: 6},
        { text: `${i18n.t('Exhibition goods')}`, value: 7},
        { text: `${i18n.t('Testing analysis')}`, value: 8},
      ],
    };
  },
  computed: {
    isDdp() {
      return this.value.goods_incoterm === 'DDP';
    },
    // ── Clearance / broker ──────────────────────────────────────────────────
    clearanceByOptions() {
      return [
        { text: this.$t('by_carrier'), value: 'carrier' },
        { text: this.$t('by_broker'), value: 'broker' },
      ];
    },
    brokerOptions() {
      const brokers = this.get_brokers.items || this.get_brokers;
      if (!brokers || !Array.isArray(brokers)) return [];
      return brokers.map((opt) => ({
        text: opt.company_name || opt.name,
        value: opt.id,
      }));
    },
    // Top info row: 3 fields normally (col-xs-4), 4 fields when broker (col-xs-3)
    infoTopRowColClass() {
      return this.value.clearance_by === 'broker' ? 'col-xs-3' : 'col-xs-4';
    },
    // ── Incoterms ───────────────────────────────────────────────────────────
    getIncotermOptions() {
      return this.incotermsOptions.filter((item) => {
        const isNotDDPWhenNoCustoms = !this.showCustoms && item.value === 'DDP';
        const isAvailable = this.availableIncoterms.length === 0 || this.availableIncoterms.includes(item.value);
        return !isNotDDPWhenNoCustoms && isAvailable;
      });
    },
    // ── Duty / Freight paid-by column logic ─────────────────────────────────
    isDutyNonSender() {
      return this.isDdp && (this.value.duty_paid_by === 2 || this.value.duty_paid_by === 3);
    },
    isFreightNonSender() {
      return this.showFreightBy && (this.value.freight_paid_by === 2 || this.value.freight_paid_by === 3);
    },
    classDutyPaidBy() {
      if (this.isDutyNonSender && this.isFreightNonSender) return 'col-xs-3';
      if (this.isDutyNonSender || this.isFreightNonSender) return 'col-xs-4';
      return 'col-xs-6';
    },
    classDutyCarrierNo() {
      if (this.isDutyNonSender && this.isFreightNonSender) return 'col-xs-3';
      return 'col-xs-4';
    },
    classFreightBy() {
      if (!this.isDdp) {
        return this.isFreightNonSender ? 'col-xs-6' : 'col-xs-12';
      }
      if (this.isDutyNonSender && this.isFreightNonSender) return 'col-xs-3';
      if (this.isDutyNonSender || this.isFreightNonSender) return 'col-xs-4';
      return 'col-xs-6';
    },
    classFreightCarrierNo() {
      if (!this.isDdp) return 'col-xs-6';
      if (this.isDutyNonSender && this.isFreightNonSender) return 'col-xs-3';
      return 'col-xs-4';
    },
    // ── Additional info (ECCN / TEXT / MRN) dynamic grid ───────────────────
    nonEeiSelectedCount() {
      return ['2', '22', '4'].filter(v => this.selectedInfoItems.indexOf(v) > -1).length;
    },
    nonEeiColClass() {
      if (this.nonEeiSelectedCount === 1) return 'col-xs-12 m-b-10';
      if (this.nonEeiSelectedCount === 2) return 'col-xs-6 m-b-10';
      return 'col-xs-4 m-b-10';
    },
    // ── Freight paid-by options ─────────────────────────────────────────────
    freightPaidByOptions() {
      const options = [];
      options.push({ text: `${this.$t('Seller')}`, value: 1 });
      if (this.get_payers && this.get_payers.items && this.get_payers.items.length > 0) {
        options.push({ text: `${this.$t('LBL_PAYER_LIST')}`, value: 4 });
      }
      options.push({ text: `${this.$t('Other')}`, value: 2 });
      return options;
    },
    ...mapGetters([
      'get_payers',
      'get_payer_select',
      'get_shippers',
      'get_brokers',
    ])
  },
  created() {
    this.$store.dispatch('GET_PAYERS_OPTIONS');
    this.$store.dispatch('GET_SHIPPERS', { type: [1] });
    this.$store.dispatch('GET_BROKERS');

    this.$store.dispatch('GET_PAYMENT_TERMS', {
      category: '23',
      incoterms: 'DDP',
    }).then((res) => {
      this.incotermServiceId = res.data.data.id;
    });

    this.$store.dispatch('GET_PAYMENT_TERMS', {
      category: '14',
      incoterms: null,
    }).then((res) => {
      this.reciverPayId = res.data.data.id;
    });

    this.$store.dispatch('GET_PAYMENT_TERMS', {
      category: '27',
      incoterms: null,
    }).then((res) => {
      this.brokerServiceId = res.data.data.id;
      if (this.value.clearance_by === 'broker') {
        if (this.incotermServices.indexOf(this.brokerServiceId) === -1) {
          this.incotermServices.push(this.brokerServiceId);
          this.$emit('incotermsServicesUpdate', this.incotermServices);
        }
      }
    });

    if (this.value.goods_incoterm !== 'DDP') {
      this.value.duty_paid_by = null;
    }
  },
  methods: {
    updateAdditionalInfo(key, value) {
      this.$emit('additional-info-update', { ...this.additionalInfo, [key]: value });
    },
    updateNestedAdditionalInfo(parentKey, key, value) {
      this.$emit('additional-info-update', {
        ...this.additionalInfo,
        [parentKey]: {
          ...this.additionalInfo[parentKey],
          [key]: value
        }
      });
    },
    selectAdditionalInfo(val) {
      const selected = Array.isArray(val) ? val.map(String) : [];
      this.$emit('selected-info-update', selected);

      // The `selectedInfoItems` prop still holds the previous selection while this handler runs
      // (props only refresh on the parent re-render), so the incoming `val` is the only reliable
      // source of what is selected. Clearing the deselected fields in a single emit also prevents
      // consecutive emits built from the same stale `additionalInfo` prop from resurrecting values
      // that a previous emit already cleared.
      // EORI is intentionally left alone: it has no option in `additionalInfoOptions` and no field of
      // its own, it is inherited from the receiver's saved shipping info.
      const clearedInfo = { ...this.additionalInfo };
      if (selected.indexOf('2') < 0) {
        clearedInfo.eccn = '';
      }
      if (selected.indexOf('3') < 0) {
        clearedInfo.eeiitn = { eei: '', tinNumber: '', tinType: '' };
      }
      if (selected.indexOf('22') < 0) {
        clearedInfo.customsAdditionalInfoText = '';
      }
      if (selected.indexOf('4') < 0) {
        clearedInfo.mrn = '';
      }
      this.$emit('additional-info-update', clearedInfo);
    },
    checkDutyAndCarrierNo(val) {
      if (val === 'DDP') {
        this.incotermServices.push(this.incotermServiceId);
        this.$emit('input', {
          customsAddress: { ...this.value.customsAddress },
          customsAddressData: { ...this.value.customsAddressData },
          goods_incoterm: val,
          duty_paid_by: 1,
          duty_payer_account_no: this.value.duty_payer_account_no,
          freight_paid_by: this.value.freight_paid_by,
          freight_payer_account_no: this.value.freight_payer_account_no,
          freight_payer_id: this.value.freight_payer_id,
        });
      } else {
        this.incotermServices.splice(this.incotermServices.indexOf(this.incotermServiceId), 1);
        // Leaving DDP: duty is only relevant for DDP, so reset the DDP-only fields instead of
        // carrying a stale duty payer / account no forward (the inputs are hidden for DAP/EXW).
        this.$emit('input', {
          customsAddress: { ...this.value.customsAddress },
          customsAddressData: { ...this.value.customsAddressData },
          goods_incoterm: val,
          duty_paid_by: null,
          duty_payer_account_no: '',
          freight_paid_by: this.value.freight_paid_by,
          freight_payer_account_no: this.value.freight_payer_account_no,
          freight_payer_id: this.value.freight_payer_id,
        });
      }
      this.$emit('incotermsServicesUpdate', this.incotermServices);
    },
    checkCarrierNo(val) {
      if (parseInt(val) !== 3) {
        this.$emit('input', {
          goods_incoterm: this.value.goods_incoterm,
          duty_paid_by: val,
          duty_payer_account_no: '',
          freight_paid_by: this.value.freight_paid_by,
          freight_payer_account_no: this.value.freight_payer_account_no,
        });
      }
    },
    checkFreightandCustomerInfo(val) {
      if (parseInt(val) !== 2) {
        this.$emit('input', {
          goods_incoterm: this.value.goods_incoterm,
          duty_paid_by: this.value.duty_paid_by,
          duty_payer_account_no: this.value.duty_payer_account_no,
          freight_paid_by: val,
          freight_payer_account_no: '',
        });
        this.incotermServices.splice(this.incotermServices.indexOf(this.reciverPayId), 1);
      }
      if (parseInt(val) === 2) {
        if (this.incotermServices.indexOf(this.reciverPayId) === -1) {
          this.incotermServices.push(this.reciverPayId);
        }
      }
      this.$emit('incotermsServicesUpdate', this.incotermServices);
    },
    onClearanceByChange(val) {
      if (val === 'broker' && this.value.broker_id) {
        if (this.brokerServiceId && this.incotermServices.indexOf(this.brokerServiceId) === -1) {
          this.incotermServices.push(this.brokerServiceId);
        }
      } else {
        const idx = this.incotermServices.indexOf(this.brokerServiceId);
        if (idx > -1) this.incotermServices.splice(idx, 1);
      }
      this.$emit('incotermsServicesUpdate', this.incotermServices);
    },
    onBrokerChange(val) {
      if (val && this.value.clearance_by === 'broker') {
        if (this.brokerServiceId && this.incotermServices.indexOf(this.brokerServiceId) === -1) {
          this.incotermServices.push(this.brokerServiceId);
        }
      } else {
        const idx = this.incotermServices.indexOf(this.brokerServiceId);
        if (idx > -1) this.incotermServices.splice(idx, 1);
      }
      this.$emit('incotermsServicesUpdate', this.incotermServices);
    },
  },
}
</script>
